import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ClipboardList,
  Search,
  BarChart3,
  MoreVertical,
  Edit,
  Trash2,
  LogOut,
  User,
  Settings,
  ChevronDown,
  Activity,
  LayoutGrid,
  Loader2,
  UserPlus,
  Shield,
  Check,
  X,
  Phone,
  Stethoscope,
  Building2,
  DollarSign
} from 'lucide-react';
import { authAPI, usersAPI, surveysAPI } from '../services/api';
import { MobileNavAdmin } from '../components/MobileNav';

const UsersPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    name: '',
    phone: '',
    organization: '',
    role: 'doctor',
    password: '',
    assigned_surveys: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      
      if (userData.role !== 'administrator') {
        navigate('/dashboard');
        return;
      }

      const usersData = await usersAPI.getAll();
      setUsers(usersData);
      
      const surveysData = await surveysAPI.getAll();
      setSurveys(surveysData);
    } catch (error) {
      if (error.response?.status === 401) navigate('/auth');
      if (error.response?.status === 403) navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await authAPI.logout();
    navigate('/auth');
  };

  const handleCreateUser = async () => {
    if (!userForm.username || !userForm.email || !userForm.password || !userForm.name) {
      alert('Please fill all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const newUser = await usersAPI.create(userForm);
      setUsers([...users, newUser]);
      setShowCreateUser(false);
      resetForm();
      alert('User created successfully!');
    } catch (error) {
      alert('Failed to create user: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    setIsSubmitting(true);
    try {
      const updates = {
        email: userForm.email,
        name: userForm.name,
        phone: userForm.phone,
        organization: userForm.organization,
        role: userForm.role,
        assigned_surveys: userForm.assigned_surveys
      };
      if (userForm.password) {
        updates.password = userForm.password;
      }
      
      const updated = await usersAPI.update(selectedUser.id, updates);
      setUsers(users.map(u => u.id === selectedUser.id ? updated : u));
      setShowEditUser(false);
      setSelectedUser(null);
      resetForm();
    } catch (error) {
      alert('Failed to update user: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;

    try {
      await usersAPI.delete(userId);
      setUsers(users.map(u => u.id === userId ? { ...u, is_active: false } : u));
    } catch (error) {
      alert('Failed to delete user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await usersAPI.activate(userId);
      setUsers(users.map(u => u.id === userId ? { ...u, is_active: true } : u));
    } catch (error) {
      alert('Failed to activate user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const openEditUser = (u) => {
    setSelectedUser(u);
    setUserForm({
      username: u.username,
      email: u.email,
      name: u.name,
      phone: u.phone || '',
      organization: u.organization || '',
      role: u.role,
      password: '',
      assigned_surveys: u.assigned_surveys || []
    });
    setShowEditUser(true);
  };

  const resetForm = () => {
    setUserForm({
      username: '',
      email: '',
      name: '',
      phone: '',
      organization: '',
      role: 'doctor',
      password: '',
      assigned_surveys: []
    });
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.phone?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadgeColor = (role) => {
    const colors = {
      administrator: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      doctor: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      agency: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      sponsor: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
    };
    return colors[role] || 'bg-slate-500/10 text-slate-400';
  };

  const getRoleIcon = (role) => {
    const icons = {
      administrator: Shield,
      doctor: Stethoscope,
      agency: Building2,
      sponsor: DollarSign
    };
    const Icon = icons[role] || User;
    return <Icon className="w-3 h-3 mr-1" />;
  };

  const isAdmin = user?.role === 'administrator';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 border-r border-slate-800 z-40 hidden lg:flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">KAP<span className="text-cyan-400">Surv</span></span>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <button onClick={() => navigate('/dashboard')} className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors">
            <LayoutGrid className="w-5 h-5" /> Dashboard
          </button>
          <button onClick={() => navigate('/surveys')} className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors">
            <ClipboardList className="w-5 h-5" /> Surveys
          </button>
          <button onClick={() => navigate('/analytics')} className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors">
            <BarChart3 className="w-5 h-5" /> Analytics
          </button>
          {isAdmin && (
            <button className="w-full flex items-center gap-3 px-4 py-3 text-white bg-white/10 rounded-xl font-medium">
              <UserPlus className="w-5 h-5" /> Manage Users
            </button>
          )}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-semibold">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">Administrator</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-800">
              <DropdownMenuItem className="gap-2 text-slate-300"><User className="w-4 h-4" /> Profile</DropdownMenuItem>
              <DropdownMenuItem className="gap-2 text-slate-300"><Settings className="w-4 h-4" /> Settings</DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem className="gap-2 text-red-400" onClick={handleLogout}><LogOut className="w-4 h-4" /> Log out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64 p-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">User Management</h1>
            <p className="text-slate-400">Manage users and their survey assignments</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-64 bg-slate-900 border-slate-800 text-white placeholder:text-slate-500 focus:border-cyan-500 rounded-xl"
              />
            </div>
            <Button onClick={() => { resetForm(); setShowCreateUser(true); }} className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 gap-2 rounded-xl">
              <UserPlus className="w-4 h-4" /> Add User
            </Button>
          </div>
        </div>

        {/* Users Table */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400">User</TableHead>
                  <TableHead className="text-slate-400">Phone</TableHead>
                  <TableHead className="text-slate-400">Organization</TableHead>
                  <TableHead className="text-slate-400">Role</TableHead>
                  <TableHead className="text-slate-400">Assigned Surveys</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((u) => (
                    <TableRow key={u.id} className="border-slate-800">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center text-cyan-400 font-semibold">
                            {u.name?.charAt(0) || 'U'}
                          </div>
                          <div>
                            <p className="font-medium text-white">{u.name}</p>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {u.phone ? (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {u.phone}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-slate-400">{u.organization || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getRoleBadgeColor(u.role)}>
                          {getRoleIcon(u.role)}
                          {u.role?.charAt(0).toUpperCase() + u.role?.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-400">{u.assigned_surveys?.length || 0} surveys</span>
                      </TableCell>
                      <TableCell>
                        {u.is_active !== false ? (
                          <Badge className="bg-emerald-500/10 text-emerald-400"><Check className="w-3 h-3 mr-1" /> Active</Badge>
                        ) : (
                          <Badge className="bg-red-500/10 text-red-400"><X className="w-3 h-3 mr-1" /> Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800">
                            <DropdownMenuItem onClick={() => openEditUser(u)} className="gap-2 text-slate-300">
                              <Edit className="w-4 h-4" /> Edit
                            </DropdownMenuItem>
                            {u.is_active === false ? (
                              <DropdownMenuItem onClick={() => handleActivateUser(u.id)} className="gap-2 text-emerald-400">
                                <Check className="w-4 h-4" /> Activate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => handleDeleteUser(u.id)} className="gap-2 text-red-400">
                                <Trash2 className="w-4 h-4" /> Deactivate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription className="text-slate-400">Add a new user and assign them to surveys.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Full Name *</Label>
                <Input value={userForm.name} onChange={(e) => setUserForm({...userForm, name: e.target.value})} placeholder="Dr. John Smith" className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Username *</Label>
                <Input value={userForm.username} onChange={(e) => setUserForm({...userForm, username: e.target.value})} placeholder="johnsmith" className="bg-slate-800 border-slate-700 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Email *</Label>
                <Input type="email" value={userForm.email} onChange={(e) => setUserForm({...userForm, email: e.target.value})} placeholder="john@hospital.org" className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Phone Number</Label>
                <Input value={userForm.phone} onChange={(e) => setUserForm({...userForm, phone: e.target.value})} placeholder="+1234567890" className="bg-slate-800 border-slate-700 text-white" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Organization</Label>
              <Input value={userForm.organization} onChange={(e) => setUserForm({...userForm, organization: e.target.value})} placeholder="Hospital or Institute" className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Role *</Label>
                <Select value={userForm.role} onValueChange={(v) => setUserForm({...userForm, role: v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="doctor" className="text-white focus:bg-slate-700 focus:text-white">Doctor</SelectItem>
                    <SelectItem value="administrator" className="text-white focus:bg-slate-700 focus:text-white">Administrator</SelectItem>
                    <SelectItem value="agency" className="text-white focus:bg-slate-700 focus:text-white">Agency</SelectItem>
                    <SelectItem value="sponsor" className="text-white focus:bg-slate-700 focus:text-white">Sponsor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Password *</Label>
                <Input type="password" value={userForm.password} onChange={(e) => setUserForm({...userForm, password: e.target.value})} placeholder="Min 6 chars" className="bg-slate-800 border-slate-700 text-white" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Assign to Surveys</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto p-2 bg-slate-800 rounded-lg">
                {surveys.length === 0 ? (
                  <p className="text-slate-500 text-sm">No surveys available</p>
                ) : (
                  surveys.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 p-2 hover:bg-slate-700 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={userForm.assigned_surveys.includes(s.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setUserForm({...userForm, assigned_surveys: [...userForm.assigned_surveys, s.id]});
                          } else {
                            setUserForm({...userForm, assigned_surveys: userForm.assigned_surveys.filter(id => id !== s.id)});
                          }
                        }}
                        className="accent-cyan-500"
                      />
                      <span className="text-slate-300">{s.title}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)} className="border-slate-700 text-slate-300">Cancel</Button>
            <Button onClick={handleCreateUser} disabled={isSubmitting} className="bg-gradient-to-r from-blue-500 to-cyan-400">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />} Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription className="text-slate-400">Update user information and survey assignments.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label className="text-slate-300">Full Name</Label>
              <Input value={userForm.name} onChange={(e) => setUserForm({...userForm, name: e.target.value})} className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Email</Label>
                <Input type="email" value={userForm.email} onChange={(e) => setUserForm({...userForm, email: e.target.value})} className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Phone Number</Label>
                <Input value={userForm.phone} onChange={(e) => setUserForm({...userForm, phone: e.target.value})} className="bg-slate-800 border-slate-700 text-white" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Organization</Label>
              <Input value={userForm.organization} onChange={(e) => setUserForm({...userForm, organization: e.target.value})} className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Role</Label>
                <Select value={userForm.role} onValueChange={(v) => setUserForm({...userForm, role: v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="doctor" className="text-white focus:bg-slate-700 focus:text-white">Doctor</SelectItem>
                    <SelectItem value="administrator" className="text-white focus:bg-slate-700 focus:text-white">Administrator</SelectItem>
                    <SelectItem value="agency" className="text-white focus:bg-slate-700 focus:text-white">Agency</SelectItem>
                    <SelectItem value="sponsor" className="text-white focus:bg-slate-700 focus:text-white">Sponsor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">New Password</Label>
                <Input type="password" value={userForm.password} onChange={(e) => setUserForm({...userForm, password: e.target.value})} placeholder="Leave blank to keep" className="bg-slate-800 border-slate-700 text-white" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Assign to Surveys</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto p-2 bg-slate-800 rounded-lg">
                {surveys.length === 0 ? (
                  <p className="text-slate-500 text-sm">No surveys available</p>
                ) : (
                  surveys.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 p-2 hover:bg-slate-700 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={userForm.assigned_surveys.includes(s.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setUserForm({...userForm, assigned_surveys: [...userForm.assigned_surveys, s.id]});
                          } else {
                            setUserForm({...userForm, assigned_surveys: userForm.assigned_surveys.filter(id => id !== s.id)});
                          }
                        }}
                        className="accent-cyan-500"
                      />
                      <span className="text-slate-300">{s.title}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditUser(false)} className="border-slate-700 text-slate-300">Cancel</Button>
            <Button onClick={handleUpdateUser} disabled={isSubmitting} className="bg-gradient-to-r from-blue-500 to-cyan-400">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />} Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Bottom Navigation */}
      <MobileNavAdmin />
      
      {/* Bottom Padding for Mobile Nav */}
      <div className="lg:hidden h-24" />
    </div>
  );
};

export default UsersPage;
